import { OpenInNewRounded } from "@mui/icons-material";
import { Chip, Tooltip } from "@mui/material";
import { ROUTES, ROUTE_SLUGS } from "src/config/navigation/routes";
import { fillRouteWithSlugs } from "src/utils";

type FrameTemplateChipProps = {
  dataTestId?: string;
  maxWidth?: number | string;
  orgId: string;
  templateId: string;
  templateTitle: string;
};

/**
 * Pill linking a document to its template ("frame"), styled like
 * `DocumentScopeChip` (plain label + icon slots so the theme's `xxs` sizing
 * applies). Shows just the template title. Opens the frame viewer in a new
 * tab — the link icon, hover treatment, and tooltip signal that it navigates.
 */
export const FrameTemplateChip = ({
  dataTestId = "frame-template-chip",
  maxWidth = 112,
  orgId,
  templateId,
  templateTitle,
}: FrameTemplateChipProps) => {
  const href = fillRouteWithSlugs(ROUTES.FRAME_VIEWER, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
    [ROUTE_SLUGS.TEMPLATE_ID]: templateId,
  });

  return (
    <Tooltip
      describeChild
      placement="top"
      title={`Open Original Frame: ${templateTitle}`}
    >
      <Chip
        clickable
        component="a"
        data-testid={dataTestId}
        href={href}
        icon={<OpenInNewRounded aria-hidden />}
        label={templateTitle}
        rel="noopener"
        size="xxs"
        target="_blank"
        variant="outlined"
        sx={{
          flex: "0 1 auto",
          maxWidth,
          minWidth: 0,
          "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
          "&:hover": {
            borderColor: "primary.main",
            color: "primary.main",
            "& .MuiChip-icon": {
              color: "primary.main",
            },
          },
        }}
      />
    </Tooltip>
  );
};
